import { useState, useEffect } from 'react';

const arrowBtn = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: 8,
  background: 'rgba(0,0,0,0.45)',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: 44,
  height: 44,
  fontSize: 28,
  lineHeight: 1,
  cursor: 'pointer',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  transition: 'background 0.2s',
});

export const MediaCarousel = ({ media, dark }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [containerHeight, setContainerHeight] = useState(null);

  useEffect(() => {
    if (!media || media.length === 0) return;
    const hasVideo = media.some(m => m.type === 'video');
    const imageItems = media.filter(m => m.type === 'image');

    if (imageItems.length === 0) {
      setContainerHeight(hasVideo ? 540 : 300);
      return;
    }

    let maxH = hasVideo ? 540 : 0;
    let pending = imageItems.length;

    imageItems.forEach(({ src }) => {
      const img = new window.Image();
      img.onload = () => {
        maxH = Math.max(maxH, Math.min(img.naturalHeight, 500));
        if (--pending === 0) setContainerHeight(maxH);
      };
      img.onerror = () => {
        if (--pending === 0) setContainerHeight(maxH || 300);
      };
      img.src = src;
    });
  }, [media]);

  if (!media || media.length === 0) return null;

  const total = media.length;
  const current = media[currentIndex];

  const goLeft = () => setCurrentIndex(i => (i - 1 + total) % total);
  const goRight = () => setCurrentIndex(i => (i + 1) % total);

  return (
    <div style={{ marginBottom: 12, userSelect: 'none' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: containerHeight ?? 300 }}>
        {total > 1 && (
          <button
            onClick={goLeft}
            style={arrowBtn('left')}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
          >‹</button>
        )}

        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: total > 1 ? '0 60px' : 0 }}>
          {current.type === 'video' ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <video
                key={current.src}
                controls
                style={{ maxWidth: '100%', maxHeight: 540, borderRadius: 8, border: '4px solid rgba(0,0,0,0.1)', display: 'block' }}
              >
                <source src={current.src} type="video/mp4" />
              </video>
              <button
                onClick={() => setIsZoomed(true)}
                title="Expand"
                style={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  background: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 13,
                  cursor: 'pointer',
                  zIndex: 5,
                }}
              >⛶ Expand</button>
            </div>
          ) : (
            <img
              src={current.src}
              alt=""
              onClick={() => setIsZoomed(true)}
              style={{
                maxWidth: '100%',
                maxHeight: 500,
                objectFit: 'contain',
                border: '4px solid rgba(0,0,0,0.1)',
                borderRadius: 8,
                background: 'white',
                cursor: 'zoom-in',
                transition: 'transform 0.2s ease-in-out',
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            />
          )}
        </div>

        {total > 1 && (
          <button
            onClick={goRight}
            style={arrowBtn('right')}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
          >›</button>
        )}
      </div>

      {total > 1 && (
        <div style={{ textAlign: 'center', color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', fontSize: 13, marginTop: 4 }}>
          {currentIndex + 1} / {total}
        </div>
      )}

      {total > 1 && (
        <div style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          padding: '10px 4px 4px',
          justifyContent: total <= 8 ? 'center' : 'flex-start',
          scrollbarWidth: 'thin',
        }}>
          {media.map((item, idx) => (
            <div
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              style={{
                cursor: 'pointer',
                border: `3px solid ${idx === currentIndex ? '#0d6efd' : 'transparent'}`,
                borderRadius: 6,
                overflow: 'hidden',
                flexShrink: 0,
                opacity: idx === currentIndex ? 1 : 0.55,
                transition: 'opacity 0.2s, border-color 0.2s',
              }}
            >
              {item.type === 'video' ? (
                <div style={{
                  width: 64,
                  height: 64,
                  background: '#1a1a1a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 22,
                }}>▶</div>
              ) : (
                <img src={item.src} alt="" style={{ width: 64, height: 64, objectFit: 'cover', display: 'block' }} />
              )}
            </div>
          ))}
        </div>
      )}

      {isZoomed && (
        <div
          onClick={e => e.target === e.currentTarget && setIsZoomed(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            animation: 'fadeIn 0.25s ease-in-out forwards',
          }}
        >
          <button
            onClick={() => setIsZoomed(false)}
            style={{
              position: 'fixed',
              top: 20,
              right: 24,
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              fontSize: 22,
              cursor: 'pointer',
              zIndex: 51,
            }}
          >✕</button>

          {total > 1 && (
            <button
              onClick={e => { e.stopPropagation(); goLeft(); }}
              style={{ ...arrowBtn('left'), left: 24 }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
            >‹</button>
          )}

          {current.type === 'video' ? (
            <video
              key={current.src}
              controls
              autoPlay
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                borderRadius: 12,
                border: '8px solid rgba(255,255,255,0.85)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                animation: 'zoomIn 0.25s ease-in-out forwards',
              }}
            >
              <source src={current.src} type="video/mp4" />
            </video>
          ) : (
            <img
              src={current.src}
              alt=""
              onClick={() => window.open(current.src, '_blank')}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                border: '8px solid rgba(255,255,255,0.85)',
                borderRadius: 12,
                background: 'white',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                animation: 'zoomIn 0.25s ease-in-out forwards',
                cursor: 'pointer',
              }}
            />
          )}

          {total > 1 && (
            <button
              onClick={e => { e.stopPropagation(); goRight(); }}
              style={{ ...arrowBtn('right'), right: 24 }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
            >›</button>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes zoomIn { from { transform: scale(0.85) } to { transform: scale(1) } }
      `}</style>
    </div>
  );
};
